if (!customElements.get('guided-ritual-finder')) {
  customElements.define('guided-ritual-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.state = { intent: this.dataset.initial === 'all' ? null : this.dataset.initial, experience: null, budget: null };
      this.started = false;
      this.busy = false;
      this.cards = [...this.querySelectorAll('[data-product]')];
      this.money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(value / 100);
      this.addEventListener('click', e => this.click(e), true);
      this.addEventListener('change', e => {
        if (e.target.matches('[data-variant]')) this.price(e.target.closest('[data-product]'));
        this.total();
      });
      this.show(this.state.intent ? 1 : 0, false);
      this.preservePreview();
    }
    analyticsAllowed() {
      try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; }
      catch (_) { return false; }
    }
    correlationId() {
      if (!this.journeyId) this.journeyId = crypto.randomUUID();
      return this.journeyId;
    }
    attribution() {
      if (!this.analyticsAllowed()) return {};
      return {
        _ritual_source: 'guided_ritual_finder',
        _ritual_journey: this.correlationId(),
        _ritual_intent: this.state.intent || '',
        _ritual_guidance: this.state.experience || '',
        _ritual_budget: this.state.budget || ''
      };
    }
    async track(name, extra = {}) {
      if (!this.analyticsAllowed()) return false;
      const payload = { ...this.state, source: 'guided_ritual_finder', journey_id: this.correlationId(), ...extra };
      const pending = [];
      try { pending.push(Promise.resolve(window.Shopify?.analytics?.publish(name, payload))); }
      catch (_) {}
      let timer;
      try {
        return await Promise.race([
          Promise.allSettled(pending).then(results => results.some(result => result.status === 'fulfilled' && result.value === true)),
          new Promise(resolve => { timer = setTimeout(() => resolve(false), 750); })
        ]);
      } finally { clearTimeout(timer); }
    }
    start() {
      if (!this.started && this.analyticsAllowed()) {
        this.started = true;
        this.track('ritual_finder_start');
      }
    }
    preservePreview() {
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview && /^\d+$/.test(preview)) {
        this.querySelectorAll('a[href]').forEach(a => {
          const url = new URL(a.href, location.href);
          if (url.origin === location.origin) { url.searchParams.set('preview_theme_id', preview); a.href = url.href; }
        });
      }
    }
    show(step, focus = true) {
      this.step = step;
      this.querySelectorAll('[data-step]').forEach(el => { el.hidden = Number(el.dataset.step) !== step; });
      this.querySelector('[data-step-number]').textContent = step + 1;
      this.querySelector('[data-back]').hidden = step === 0;
      this.querySelector('[data-intent-copy]').textContent = this.copy.intents[this.state.intent] || '';
      ['intent', 'experience', 'budget'].forEach(key => this.querySelectorAll(`[data-${key}]`).forEach(el => el.setAttribute('aria-pressed', String(el.dataset[key] === this.state[key]))));
      this.status('');
      if (focus) this.querySelector(`[data-step="${step}"] h3`).focus({ preventScroll: false });
    }
    click(e) {
      const button = e.target.closest('button');
      const link = e.target.closest('[data-product-link]');
      if (link) { this.start(); this.track('ritual_product_clicked', { product_id: link.closest('[data-product]').dataset.product }); }
      if (!button || !this.contains(button) || this.busy) return;
      this.start();
      for (const key of ['intent', 'experience', 'budget']) {
        if (button.dataset[key]) {
          this.state[key] = button.dataset[key];
          this.track(`ritual_${key === 'experience' ? 'experience' : key}_selected`);
          if (key === 'budget') this.results(); else this.show(key === 'intent' ? 1 : 2);
          return;
        }
      }
      if (button.hasAttribute('data-back')) this.show(Math.max(0, this.step - 1));
      if (button.hasAttribute('data-reset')) {
        this.state = { intent: null, experience: null, budget: null };
        this.querySelectorAll('[data-include]').forEach(el => { el.checked = false; });
        this.show(0);
      }
      if (button.dataset.related) { this.state.intent = button.dataset.related; this.state.budget = null; this.track('ritual_intent_selected'); this.show(2); }
      if (button.hasAttribute('data-add')) this.add([button.closest('[data-product]')], false, button);
      if (button.hasAttribute('data-bundle')) this.add(this.cards.filter(card => !card.hidden && card.querySelector('[data-include]').checked), true, button);
    }
    within(price) {
      switch (this.state.budget) {
        case 'under25': return price < 2500;
        case '25to50': return price >= 2500 && price < 5000;
        case '50to100': return price >= 5000 && price <= 10000;
        default: return true;
      }
    }
    results() {
      let count = 0;
      this.cards.forEach(card => {
        const select = card.querySelector('[data-variant]');
        const options = [...select.options];
        options.forEach(opt => { opt.disabled = !this.within(Number(opt.dataset.price)); opt.hidden = opt.disabled; });
        const first = options.find(opt => !opt.disabled);
        card.hidden = card.dataset.mapping !== this.state.intent || !first;
        card.querySelector('[data-include]').checked = false;
        if (!card.hidden) { if (select.selectedOptions[0]?.disabled) select.value = first.value; count++; this.price(card); }
      });
      this.querySelector('[data-empty]').hidden = count > 0;
      this.querySelector('[data-bundle-bar]').hidden = count === 0;
      this.querySelectorAll('[data-guidance]').forEach(el => { el.hidden = el.dataset.guidance !== this.state.experience; });
      this.querySelectorAll('[data-related]').forEach(el => { el.hidden = el.dataset.related === this.state.intent; });
      this.total(); this.show(3); this.track('ritual_results_viewed', { product_count: count, product_ids: this.cards.filter(card => !card.hidden).map(card => card.dataset.product), variant_ids: this.cards.filter(card => !card.hidden).map(card => card.querySelector('[data-variant]').value) });
    }
    price(card) {
      const option = card.querySelector('[data-variant]').selectedOptions[0];
      card.querySelector('[data-price-text]').textContent = this.money(Number(option.dataset.price));
    }
    total() {
      const selected = this.cards.filter(card => !card.hidden && card.querySelector('[data-include]').checked);
      const total = selected.reduce((sum, card) => sum + Number(card.querySelector('[data-variant]').selectedOptions[0].dataset.price), 0);
      this.querySelector('[data-total]').textContent = this.money(total);
      this.querySelector('[data-bundle]').disabled = this.busy || selected.length === 0;
      this.querySelector('[data-bundle-bar]').classList.toggle('ritual__bundle--selected', selected.length > 0);
    }
    status(message) { this.querySelector('[data-status]').textContent = message; }
    async request(url, options = {}) {
      const controller = new AbortController(); const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal, credentials: 'same-origin' });
        const data = await response.json();
        if (!response.ok) throw new Error(this.copy.messages.error);
        return data;
      } finally { clearTimeout(timeout); }
    }
    async add(cards, bundle, trigger) {
      if (!cards.length) { this.status(this.copy.messages.none); return; }
      this.busy = true; this.setAttribute('aria-busy', 'true');
      const controls = [...this.querySelectorAll('button,select,input')];
      const disabled = controls.map(el => el.disabled); controls.forEach(el => { el.disabled = true; });
      this.status(this.copy.messages.busy);
      let posted = false;
      try {
        const items = [];
        for (const card of cards) {
          const option = card.querySelector('[data-variant]').selectedOptions[0];
          const product = await this.request(`${this.dataset.root}products/${encodeURIComponent(card.dataset.handle)}.js`);
          const variant = product.variants.find(v => String(v.id) === option.value);
          if (!variant?.available) throw new Error(this.copy.messages.unavailable);
          if (variant.price !== Number(option.dataset.price)) throw new Error(this.copy.messages.changed);
          items.push({ id: variant.id, quantity: 1, properties: this.attribution() });
        }
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = cart?.getSectionsToRender?.().map(section => section.id) || ['cart-icon-bubble'];
        if (cart?.setActiveElement) cart.setActiveElement(trigger);
        this.status(this.copy.messages.adding); posted = true;
        const result = await this.request(`${this.dataset.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: location.pathname }) });
        await this.track(bundle ? 'ritual_bundle_added' : 'ritual_add_to_cart', { variant_ids: items.map(item => String(item.id)), item_count: items.length });
        this.status(this.copy.messages.added); this.querySelector('[data-cart-link]').hidden = false;
        if (cart && (!bundle || cart.tagName === 'CART-DRAWER')) {
          try { cart.renderContents({ ...(result.items?.[0] || result), sections: result.sections }); } catch (_) {}
        } else {
          location.assign(this.querySelector('[data-cart-link]').href);
        }
      } catch (error) {
        this.status(posted ? this.copy.messages.error : error.message || this.copy.messages.error);
        this.querySelector('[data-cart-link]').hidden = false;
      } finally {
        this.busy = false; this.removeAttribute('aria-busy');
        controls.forEach((el, index) => { el.disabled = disabled[index]; }); this.total();
      }
    }
  });
}
